"use client";

import { type FormEvent, useState, useEffect } from "react";
import { Button } from "../design-system/atoms/Button";
import { Input } from "../design-system/atoms/Input";
import Textarea from "../design-system/atoms/Textarea";
import { Checkbox } from "../design-system/atoms/Checkbox";
import { BlockProps } from "./blocks/registry";
import { geoService, type Prediction } from "../services/geoService";
import { useFormValidation, type ValidationRules } from "../hooks/useFormValidation";
import {
  checked,
  email as emailValidator,
  personName,
  phone as phoneValidator,
  required,
} from "../utils/validation";
import Image from "next/image";

// Icônes
const ICONS = {

  user: `https://site-lms.fra1.digitaloceanspaces.com/user_45eec5a949.svg`,
  email: `https://site-lms.fra1.digitaloceanspaces.com/Email_black_b9614703cb.svg`,
  phone: `https://site-lms.fra1.digitaloceanspaces.com/Phone_black_3e770a13cf.svg`,
  adresse: `https://site-lms.fra1.digitaloceanspaces.com/Mail_black_6acf0d9eb5.svg`,
  message: `https://site-lms.fra1.digitaloceanspaces.com/Message_e699b86cd5.svg`,
  date: `https://fra1.digitaloceanspaces.com/site-lms/date_49bba0ccbe.svg`,
  structure: `https://fra1.digitaloceanspaces.com/site-lms/building_2_1205ab553e.svg`,
  salad: `https://fra1.digitaloceanspaces.com/site-lms/salad_f6030e8d9a.svg`,

  typeAutre: `https://site-lms.fra1.digitaloceanspaces.com/autre_c4d6979599.svg`,
  typeDocuments: `https://site-lms.fra1.digitaloceanspaces.com/documents_77088495a0.svg`,
  typeDevis: `https://site-lms.fra1.digitaloceanspaces.com/demande_devis_10576391d4.svg`,
  typePrescrire: `https://site-lms.fra1.digitaloceanspaces.com/prescrire_db30c06b8d.svg`,
} as const;

// Noms des champs du formulaire (réutilisation des variables d'environnement du formulaire Contact)
const FORM_FIELDS = {
  // Champs de base
  LASTNAME: process.env.NEXT_PUBLIC_SF_LASTNAME_ID,
  FIRSTNAME: process.env.NEXT_PUBLIC_SF_FIRSTNAME_ID,
  STREET: process.env.NEXT_PUBLIC_SF_STREET_ID,
  CITY: process.env.NEXT_PUBLIC_SF_CITY_ID,
  POSTCODE: process.env.NEXT_PUBLIC_SF_POSTCODE_ID,
  ACCOUNT_ID: process.env.NEXT_PUBLIC_SF_ACCOUNT_ID_ID,
  EMAIL: "email",
  PHONE: "phone",
  MESSAGE: "description",
  STRUCTURE: "structure",

  // Consentements
  CONSENT_GDPR: "consent_gdpr",
  CONSENT_PHONE: "consent_phone",
  CONSENT_EMAIL: "consent_email",

  // Paramètres UTM
  UTM_SOURCE: "utm_source",
  UTM_CAMPAIGN: "utm_campaign",
  UTM_CONTENT: "utm_content",
  UTM_TERM: "utm_term",
  UTM_MEDIUM: "utm_medium",
  GCLID: "gclid",

  // Canal d'acquisition
  ACQUISITION_CHANNEL: process.env.NEXT_PUBLIC_SF_ACQUISITION_CHANNEL_ID,

  // Indicatif téléphonique
  PHONE_INDICATOR: process.env.NEXT_PUBLIC_SF_PHONE_INDICATOR_ID,

  // Champs spécifiques
  TYPE_DEMANDE: "type_demande",

  // Prescripteur
  PRESCRIPTEUR_PRENOM: "prescripteur_prenom",
  PRESCRIPTEUR_NOM: "prescripteur_nom",
  PRESCRIPTEUR_STRUCTURE: "prescripteur_structure",
  PRESCRIPTEUR_EMAIL: "prescripteur_email",
  PRESCRIPTEUR_TELEPHONE: "prescripteur_telephone",
  PRESCRIPTEUR_MESSAGE: "prescripteur_message",

  // Patient
  PATIENT_PRENOM: "patient_prenom",
  PATIENT_NOM: "patient_nom",
  PATIENT_STRUCTURE: "patient_structure",
  PATIENT_EMAIL: "patient_email",
  PATIENT_TELEPHONE: "patient_telephone",
  PATIENT_REGIME: "patient_regime",
  PATIENT_DATE_COMMENCEMENT: "patient_date_commencement",
  PATIENT_ADRESSE: "patient_adresse",

  // Aidant
  AIDANT_PRENOM: "aidant_prenom",
  AIDANT_NOM: "aidant_nom",
  AIDANT_STRUCTURE: "aidant_structure",
  AIDANT_EMAIL: "aidant_email",
  AIDANT_TELEPHONE: "aidant_telephone",

  // Champs simples (pour "Autre" et "Demande de documents")
  PRENOM: "prenom",
  NOM: "nom",
  TELEPHONE: "telephone",
  ADRESSE: "adresse",
} as const;

// Types de demande
const DEMANDE_TYPES = [
  { value: "Demande de documents", label: "Demande de documents" },
  { value: "Demande de devis", label: "Demande de devis" },
  { value: "Prescrire", label: "Prescrire" },
  { value: "Autre", label: "Autre" },
];

interface DemandeFormProps {
  id?: number | string;
  documentId?: string;
  __component?: string;
  [key: string]: unknown;
  titre?: string;
}
type PayloadValue = string | boolean | null | undefined;

type DemandePayload = Record<string, PayloadValue>;

export default function DemandeForm({ data }: BlockProps<DemandeFormProps>) {
  const titre = data?.titre || "Faites votre demande en ligne";

  const [showAidant, setShowAidant] = useState(false);
  //const [showPatient, setShowPatient] = useState(false);
  const [patientAddressDetails, setPatientAddressDetails] = useState({ street: "", postcode: "", city: "", idSalesforce: "" });
  const [addressDetails, setAddressDetails] = useState({ street: "", postcode: "", city: "", idSalesforce: "" });

  // États pour les prédictions d'adresse (comme Contact.tsx)
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [patientPredictions, setPatientPredictions] = useState<Prediction[]>([]);
  const [showPatientPredictions, setShowPatientPredictions] = useState(false);
  const [formData, setFormData] = useState({
    [FORM_FIELDS.TYPE_DEMANDE]: "Demande de documents",
    // Prescripteur
    [FORM_FIELDS.PRESCRIPTEUR_PRENOM]: "",
    [FORM_FIELDS.PRESCRIPTEUR_NOM]: "",
    [FORM_FIELDS.PRESCRIPTEUR_STRUCTURE]: "",
    [FORM_FIELDS.PRESCRIPTEUR_EMAIL]: "",
    [FORM_FIELDS.PRESCRIPTEUR_TELEPHONE]: "",
    [FORM_FIELDS.PRESCRIPTEUR_MESSAGE]: "",
    // Patient
    [FORM_FIELDS.PATIENT_PRENOM]: "",
    [FORM_FIELDS.PATIENT_NOM]: "",
    [FORM_FIELDS.PATIENT_STRUCTURE]: "",
    [FORM_FIELDS.PATIENT_EMAIL]: "",
    [FORM_FIELDS.PATIENT_TELEPHONE]: "",
    [FORM_FIELDS.PATIENT_REGIME]: "",
    [FORM_FIELDS.PATIENT_DATE_COMMENCEMENT]: "",
    [FORM_FIELDS.PATIENT_ADRESSE]: "",
    // Aidant
    [FORM_FIELDS.AIDANT_PRENOM]: "",
    [FORM_FIELDS.AIDANT_NOM]: "",
    [FORM_FIELDS.AIDANT_STRUCTURE]: "",
    [FORM_FIELDS.AIDANT_EMAIL]: "",
    [FORM_FIELDS.AIDANT_TELEPHONE]: "",
    // Adresse (pour "Autre" et "Demande de documents")
    [FORM_FIELDS.ADRESSE]: "",
    // Consentements
    [FORM_FIELDS.CONSENT_GDPR]: false,
    [FORM_FIELDS.CONSENT_PHONE]: false,
    [FORM_FIELDS.CONSENT_EMAIL]: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const validationRules: ValidationRules = {
    [FORM_FIELDS.PRESCRIPTEUR_STRUCTURE]: [
      required("Veuillez saisir le nom de la structure."),
    ],
    [FORM_FIELDS.PRESCRIPTEUR_PRENOM]: [
      required("Veuillez saisir votre prénom."),
      personName("Veuillez saisir un prénom valide (au moins 2 lettres)."),
    ],
    [FORM_FIELDS.PRESCRIPTEUR_NOM]: [
      required("Veuillez saisir votre nom."),
      personName("Veuillez saisir un nom valide (au moins 2 lettres)."),
    ],
    [FORM_FIELDS.PRESCRIPTEUR_EMAIL]: [
      required("Veuillez saisir votre adresse e-mail."),
      emailValidator(),
    ],
    [FORM_FIELDS.PRESCRIPTEUR_TELEPHONE]: [
      required("Veuillez saisir votre téléphone."),
      phoneValidator("Veuillez saisir un numéro de téléphone français valide."),
    ],
    [FORM_FIELDS.ADRESSE]: [
      required("Veuillez saisir votre adresse de livraison."),
    ],
    [FORM_FIELDS.PRESCRIPTEUR_MESSAGE]: [
      required("Veuillez saisir votre message."),
    ],
    [FORM_FIELDS.PATIENT_STRUCTURE]: [
      required("Veuillez saisir le nom de la structure du patient."),
    ],
    [FORM_FIELDS.PATIENT_PRENOM]: [
      required("Veuillez saisir le prénom du patient."),
      personName("Veuillez saisir un prénom valide (au moins 2 lettres)."),
    ],
    [FORM_FIELDS.PATIENT_NOM]: [
      required("Veuillez saisir le nom du patient."),
      personName("Veuillez saisir un nom valide (au moins 2 lettres)."),
    ],
    [FORM_FIELDS.PATIENT_EMAIL]: [
      required("Veuillez saisir l'e-mail du patient."),
      emailValidator(),
    ],
    [FORM_FIELDS.PATIENT_TELEPHONE]: [
      required("Veuillez saisir le téléphone du patient."),
      phoneValidator("Veuillez saisir un numéro de téléphone français valide."),
    ],
    [FORM_FIELDS.PATIENT_REGIME]: [
      required("Veuillez saisir le régime du patient."),
    ],
    [FORM_FIELDS.PATIENT_DATE_COMMENCEMENT]: [
      required("Veuillez saisir la date de commencement."),
    ],
    [FORM_FIELDS.PATIENT_ADRESSE]: [
      required("Veuillez saisir l'adresse de livraison du patient."),
    ],
    [FORM_FIELDS.AIDANT_STRUCTURE]: [
      required("Veuillez saisir le nom de la structure de l'aidant."),
    ],
    [FORM_FIELDS.AIDANT_PRENOM]: [
      required("Veuillez saisir le prénom de l'aidant."),
      personName("Veuillez saisir un prénom valide (au moins 2 lettres)."),
    ],
    [FORM_FIELDS.AIDANT_NOM]: [
      required("Veuillez saisir le nom de l'aidant."),
      personName("Veuillez saisir un nom valide (au moins 2 lettres)."),
    ],
    [FORM_FIELDS.AIDANT_EMAIL]: [
      required("Veuillez saisir l'e-mail de l'aidant."),
      emailValidator(),
    ],
    [FORM_FIELDS.AIDANT_TELEPHONE]: [
      required("Veuillez saisir le téléphone de l'aidant."),
      phoneValidator("Veuillez saisir un numéro de téléphone français valide."),
    ],
    [FORM_FIELDS.CONSENT_GDPR]: [
      checked("Vous devez accepter l'utilisation de vos données."),
    ],
    [FORM_FIELDS.CONSENT_PHONE]: [],
    [FORM_FIELDS.CONSENT_EMAIL]: [],
  };

  const { errors, validateField, validateAll, clearErrors } =
    useFormValidation(validationRules);

  // Liste des champs actuellement affichés (dépend du type de demande)
  const getVisibleFieldNames = (): string[] => {
    const type = formData[FORM_FIELDS.TYPE_DEMANDE];
    const names: string[] = [
      FORM_FIELDS.PRESCRIPTEUR_STRUCTURE,
      FORM_FIELDS.PRESCRIPTEUR_PRENOM,
      FORM_FIELDS.PRESCRIPTEUR_NOM,
      FORM_FIELDS.PRESCRIPTEUR_EMAIL,
      FORM_FIELDS.PRESCRIPTEUR_TELEPHONE,
      FORM_FIELDS.PRESCRIPTEUR_MESSAGE,
      FORM_FIELDS.CONSENT_GDPR,
      FORM_FIELDS.CONSENT_PHONE,
      FORM_FIELDS.CONSENT_EMAIL,
    ];

    if (type === "Autre" || type === "Demande de documents") {
      names.push(FORM_FIELDS.ADRESSE);
    }

    if (type === "Demande de devis" || type === "Prescrire") {
      names.push(
        FORM_FIELDS.PATIENT_STRUCTURE,
        FORM_FIELDS.PATIENT_PRENOM,
        FORM_FIELDS.PATIENT_NOM,
        FORM_FIELDS.PATIENT_EMAIL,
        FORM_FIELDS.PATIENT_TELEPHONE,
        FORM_FIELDS.PATIENT_REGIME,
        FORM_FIELDS.PATIENT_DATE_COMMENCEMENT,
        FORM_FIELDS.PATIENT_ADRESSE,
      );
    }

    if (type === "Prescrire" && showAidant) {
      names.push(
        FORM_FIELDS.AIDANT_STRUCTURE,
        FORM_FIELDS.AIDANT_PRENOM,
        FORM_FIELDS.AIDANT_NOM,
        FORM_FIELDS.AIDANT_EMAIL,
        FORM_FIELDS.AIDANT_TELEPHONE,
      );
    }

    return names;
  };

  const adresseValue = formData[FORM_FIELDS.ADRESSE] || "";

  // Fetch address predictions pour "Autre" et "Demande de documents"
  useEffect(() => {
    const handler = setTimeout(async () => {
      if (adresseValue.length >= 3) {
        try {
          const data = await geoService.getPredictions(adresseValue);
          const addresses = data.filter((d: Prediction) =>
            d.types.some((t) =>
              ["subpremise", "street_address", "geocode"].includes(t)
            )
          );
          setPredictions(addresses);
          setShowPredictions(true);
        } catch (err) {
          console.error("Erreur API predictions:", err);
        }
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [adresseValue]);

  // Fetch address predictions pour Patient
  useEffect(() => {
    const handler = setTimeout(async () => {
      if (formData[FORM_FIELDS.PATIENT_ADRESSE] && formData[FORM_FIELDS.PATIENT_ADRESSE].length >= 3) {
        try {
          const data = await geoService.getPredictions(formData[FORM_FIELDS.PATIENT_ADRESSE]);
          const addresses = data.filter((d: Prediction) =>
            d.types.some((t) =>
              ["subpremise", "street_address", "geocode"].includes(t)
            )
          );
          setPatientPredictions(addresses);
          setShowPatientPredictions(true);
        } catch (err) {
          console.error("Erreur API predictions:", err);
        }
      } else {
        setPatientPredictions([]);
        setShowPatientPredictions(false);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [formData[FORM_FIELDS.PATIENT_ADRESSE]]);

  // Copier automatiquement la structure du prescripteur dans la structure du patient et de l'aidant
  useEffect(() => {
    if (formData[FORM_FIELDS.TYPE_DEMANDE] === "Prescrire" || formData[FORM_FIELDS.TYPE_DEMANDE] === "Demande de devis") {
      const prescripteurStructure = formData[FORM_FIELDS.PRESCRIPTEUR_STRUCTURE];
      if (prescripteurStructure) {
        setFormData((prev) => {
          const updated = { ...prev };
          
          // Copier vers la structure du patient si différent
          if (prescripteurStructure !== prev[FORM_FIELDS.PATIENT_STRUCTURE]) {
            updated[FORM_FIELDS.PATIENT_STRUCTURE] = prescripteurStructure;
            console.log('🔄 Copie automatique - Structure prescripteur → Structure patient:', prescripteurStructure);
          }
          
          // Copier vers la structure de l'aidant si différent
          if (prescripteurStructure !== prev[FORM_FIELDS.AIDANT_STRUCTURE]) {
            updated[FORM_FIELDS.AIDANT_STRUCTURE] = prescripteurStructure;
            console.log('🔄 Copie automatique - Structure prescripteur → Structure aidant:', prescripteurStructure);
          }
          
          return updated;
        });
      }
    }
  }, [formData[FORM_FIELDS.PRESCRIPTEUR_STRUCTURE], formData[FORM_FIELDS.TYPE_DEMANDE]]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    const fieldValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: fieldValue }));
    // Validation en temps réel pendant la saisie
    validateField(name, fieldValue);
  };
  const getAgencyIdSalesforce = async (placeId: string) => {
    try {
      const agency = await geoService.getMap(placeId);

      if (agency && agency.id) {
        return agency.idSalesforce || "";
      }

      const nearestAgency = await geoService.getNearestAgency(placeId);

      return nearestAgency?.idSalesforce || "";
    } catch (error) {
      console.error("Erreur getMap/getNearestAgency:", error);
      return "";
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validation de tous les champs visibles avant envoi
    if (!validateAll(formData, getVisibleFieldNames())) {
      setStatus("idle");
      return;
    }

    setIsSubmitting(true);
    setStatus("idle");
    setErrorMessage("");

    try {
      // Get UTM parameters and gclid from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get("utm_source");
      const utmCampaign = urlParams.get("utm_campaign");
      const utmContent = urlParams.get("utm_content");
      const utmTerm = urlParams.get("utm_term");
      const utmMedium = urlParams.get("utm_medium");
      const gclid = urlParams.get("gclid");

      // Récupérer l'ID de l'agence depuis les détails d'adresse
      let idSalesforce = "";

      // Utiliser l'ID de l'adresse patient si disponible, sinon l'adresse prescripteur
      if (formData[FORM_FIELDS.TYPE_DEMANDE] === "Demande de devis" || formData[FORM_FIELDS.TYPE_DEMANDE] === "Prescrire") {
        idSalesforce = patientAddressDetails.idSalesforce;
      } else {
        idSalesforce = addressDetails.idSalesforce;
      }

      console.log('� ID AGENCE récupéré depuis l\'API - idSalesforce =', idSalesforce);
      console.log('� TYPE DE idSalesforce =', typeof idSalesforce);

      // DEBUG: Vérifier l'état des address details avant construction
      console.log('🔍 ÉTAT PATIENT ADDRESS DETAILS AVANT PAYLOAD:', patientAddressDetails);
      console.log('🔍 ÉTAT ADDRESS DETAILS AVANT PAYLOAD:', addressDetails);

      let payload: DemandePayload  = {
        [FORM_FIELDS.TYPE_DEMANDE]: formData[FORM_FIELDS.TYPE_DEMANDE],
        [FORM_FIELDS.CONSENT_GDPR]: formData[FORM_FIELDS.CONSENT_GDPR],
        [FORM_FIELDS.CONSENT_PHONE]: formData[FORM_FIELDS.CONSENT_PHONE],
        [FORM_FIELDS.CONSENT_EMAIL]: formData[FORM_FIELDS.CONSENT_EMAIL],
        [FORM_FIELDS.UTM_SOURCE]: utmSource || "",
        [FORM_FIELDS.UTM_CAMPAIGN]: utmCampaign || "",
        [FORM_FIELDS.UTM_CONTENT]: utmContent || "",
        [FORM_FIELDS.UTM_TERM]: utmTerm || "",
        [FORM_FIELDS.UTM_MEDIUM]: utmMedium || "",
        [FORM_FIELDS.GCLID]: gclid || "",
        ...(FORM_FIELDS.ACQUISITION_CHANNEL && { [FORM_FIELDS.ACQUISITION_CHANNEL]: "Formulaire" }),
        ...(FORM_FIELDS.PHONE_INDICATOR && { [FORM_FIELDS.PHONE_INDICATOR]: "FR" }),
        ...(FORM_FIELDS.ACCOUNT_ID && { [FORM_FIELDS.ACCOUNT_ID]: idSalesforce }),
        acquisition_channel: "Formulaire",
        phone_indicator: "FR",
        AccountID: idSalesforce,
      };

      // Ajouter les champs selon le type de demande
      if (formData[FORM_FIELDS.TYPE_DEMANDE] === "Autre" || formData[FORM_FIELDS.TYPE_DEMANDE] === "Demande de documents") {
        // Pour "Autre" et "Demande de documents" : champs simples sans préfixe prescripteur
        payload = {
          ...payload,
          [FORM_FIELDS.NOM]: formData[FORM_FIELDS.PRESCRIPTEUR_NOM],
          [FORM_FIELDS.PRENOM]: formData[FORM_FIELDS.PRESCRIPTEUR_PRENOM],
          [FORM_FIELDS.STRUCTURE]: formData[FORM_FIELDS.PRESCRIPTEUR_STRUCTURE],
          [FORM_FIELDS.EMAIL]: formData[FORM_FIELDS.PRESCRIPTEUR_EMAIL],
          [FORM_FIELDS.TELEPHONE]: formData[FORM_FIELDS.PRESCRIPTEUR_TELEPHONE],
          [FORM_FIELDS.PRESCRIPTEUR_MESSAGE]: formData[FORM_FIELDS.PRESCRIPTEUR_MESSAGE],
          // Champs d'adresse splitée pour Strapi
          street: addressDetails.street,
          postcode: addressDetails.postcode,
          city: addressDetails.city,
          // Champs Salesforce pour adresse
          ...(FORM_FIELDS.STREET && { [FORM_FIELDS.STREET]: addressDetails.street }),
          ...(FORM_FIELDS.CITY && { [FORM_FIELDS.CITY]: addressDetails.city }),
          ...(FORM_FIELDS.POSTCODE && { [FORM_FIELDS.POSTCODE]: addressDetails.postcode }),
          // DEBUG: Log des champs d'adresse avant envoi
          ...(console.log('📤 ENVOI À STRAPI - Adresse (Autre/Demande):', { street: addressDetails.street, postcode: addressDetails.postcode, city: addressDetails.city }), {}),
          // Champs normalisés pour Strapi
          firstname: formData[FORM_FIELDS.PRESCRIPTEUR_PRENOM],
          lastname: formData[FORM_FIELDS.PRESCRIPTEUR_NOM],
          // Champs Salesforce pour nom et prénom
          ...(FORM_FIELDS.LASTNAME && { [FORM_FIELDS.LASTNAME]: formData[FORM_FIELDS.PRESCRIPTEUR_NOM] }),
          ...(FORM_FIELDS.FIRSTNAME && { [FORM_FIELDS.FIRSTNAME]: formData[FORM_FIELDS.PRESCRIPTEUR_PRENOM] }),
        };
      } else if (formData[FORM_FIELDS.TYPE_DEMANDE] === "Demande de devis") {
        // Pour "Demande de devis" : prescripteur + patient (+ aidant si disponible)
        payload = {
          ...payload,
          // Champs normalisés pour Strapi
          firstname: formData[FORM_FIELDS.PRESCRIPTEUR_PRENOM],
          lastname: formData[FORM_FIELDS.PRESCRIPTEUR_NOM],
          [FORM_FIELDS.PRESCRIPTEUR_PRENOM]: formData[FORM_FIELDS.PRESCRIPTEUR_PRENOM],
          [FORM_FIELDS.PRESCRIPTEUR_NOM]: formData[FORM_FIELDS.PRESCRIPTEUR_NOM],
          [FORM_FIELDS.PRESCRIPTEUR_STRUCTURE]: formData[FORM_FIELDS.PRESCRIPTEUR_STRUCTURE],
          [FORM_FIELDS.PRESCRIPTEUR_EMAIL]: formData[FORM_FIELDS.PRESCRIPTEUR_EMAIL],
          [FORM_FIELDS.PRESCRIPTEUR_TELEPHONE]: formData[FORM_FIELDS.PRESCRIPTEUR_TELEPHONE],
          [FORM_FIELDS.PRESCRIPTEUR_MESSAGE]: formData[FORM_FIELDS.PRESCRIPTEUR_MESSAGE],
          // Ajouter les données du patient
          [FORM_FIELDS.PATIENT_PRENOM]: formData[FORM_FIELDS.PATIENT_PRENOM],
          [FORM_FIELDS.PATIENT_NOM]: formData[FORM_FIELDS.PATIENT_NOM],
          [FORM_FIELDS.PATIENT_STRUCTURE]: formData[FORM_FIELDS.PATIENT_STRUCTURE],
          [FORM_FIELDS.PATIENT_EMAIL]: formData[FORM_FIELDS.PATIENT_EMAIL],
          [FORM_FIELDS.PATIENT_TELEPHONE]: formData[FORM_FIELDS.PATIENT_TELEPHONE],
          [FORM_FIELDS.PATIENT_REGIME]: formData[FORM_FIELDS.PATIENT_REGIME],
          [FORM_FIELDS.PATIENT_DATE_COMMENCEMENT]: formData[FORM_FIELDS.PATIENT_DATE_COMMENCEMENT],
          // Champs d'adresse splitée pour Strapi
          street: patientAddressDetails.street,
          postcode: patientAddressDetails.postcode,
          city: patientAddressDetails.city,
          // Champs Salesforce pour adresse
          ...(process.env.NEXT_PUBLIC_SF_STREET_ID && { [process.env.NEXT_PUBLIC_SF_STREET_ID]: patientAddressDetails.street }),
          ...(process.env.NEXT_PUBLIC_SF_CITY_ID && { [process.env.NEXT_PUBLIC_SF_CITY_ID]: patientAddressDetails.city }),
          ...(process.env.NEXT_PUBLIC_SF_POSTCODE_ID && { [process.env.NEXT_PUBLIC_SF_POSTCODE_ID]: patientAddressDetails.postcode }),
          // Champs Salesforce pour prescripteur
          ...(FORM_FIELDS.LASTNAME && { [FORM_FIELDS.LASTNAME]: formData[FORM_FIELDS.PRESCRIPTEUR_NOM] }),
          ...(FORM_FIELDS.FIRSTNAME && { [FORM_FIELDS.FIRSTNAME]: formData[FORM_FIELDS.PRESCRIPTEUR_PRENOM] }),
        };
      } else if (formData[FORM_FIELDS.TYPE_DEMANDE] === "Prescrire") {
        // Pour "Prescrire" : prescripteur + patient + aidant
        payload = {
          ...payload,
          // Champs normalisés pour Strapi
          firstname: formData[FORM_FIELDS.PRESCRIPTEUR_PRENOM],
          lastname: formData[FORM_FIELDS.PRESCRIPTEUR_NOM],
          [FORM_FIELDS.PRESCRIPTEUR_PRENOM]: formData[FORM_FIELDS.PRESCRIPTEUR_PRENOM],
          [FORM_FIELDS.PRESCRIPTEUR_NOM]: formData[FORM_FIELDS.PRESCRIPTEUR_NOM],
          [FORM_FIELDS.PRESCRIPTEUR_STRUCTURE]: formData[FORM_FIELDS.PRESCRIPTEUR_STRUCTURE],
          [FORM_FIELDS.PRESCRIPTEUR_EMAIL]: formData[FORM_FIELDS.PRESCRIPTEUR_EMAIL],
          [FORM_FIELDS.PRESCRIPTEUR_TELEPHONE]: formData[FORM_FIELDS.PRESCRIPTEUR_TELEPHONE],
          [FORM_FIELDS.PRESCRIPTEUR_MESSAGE]: formData[FORM_FIELDS.PRESCRIPTEUR_MESSAGE],
          [FORM_FIELDS.PATIENT_PRENOM]: formData[FORM_FIELDS.PATIENT_PRENOM],
          [FORM_FIELDS.PATIENT_NOM]: formData[FORM_FIELDS.PATIENT_NOM],
          [FORM_FIELDS.PATIENT_STRUCTURE]: formData[FORM_FIELDS.PATIENT_STRUCTURE],
          [FORM_FIELDS.PATIENT_EMAIL]: formData[FORM_FIELDS.PATIENT_EMAIL],
          [FORM_FIELDS.PATIENT_TELEPHONE]: formData[FORM_FIELDS.PATIENT_TELEPHONE],
          [FORM_FIELDS.PATIENT_REGIME]: formData[FORM_FIELDS.PATIENT_REGIME],
          [FORM_FIELDS.PATIENT_DATE_COMMENCEMENT]: formData[FORM_FIELDS.PATIENT_DATE_COMMENCEMENT],
          // Champs d'adresse splitée pour Strapi
          street: patientAddressDetails.street,
          postcode: patientAddressDetails.postcode,
          city: patientAddressDetails.city,
          // Champs Salesforce pour adresse
          ...(process.env.NEXT_PUBLIC_SF_STREET_ID && { [process.env.NEXT_PUBLIC_SF_STREET_ID]: patientAddressDetails.street }),
          ...(process.env.NEXT_PUBLIC_SF_CITY_ID && { [process.env.NEXT_PUBLIC_SF_CITY_ID]: patientAddressDetails.city }),
          ...(process.env.NEXT_PUBLIC_SF_POSTCODE_ID && { [process.env.NEXT_PUBLIC_SF_POSTCODE_ID]: patientAddressDetails.postcode }),
          [FORM_FIELDS.AIDANT_PRENOM]: formData[FORM_FIELDS.AIDANT_PRENOM],
          [FORM_FIELDS.AIDANT_NOM]: formData[FORM_FIELDS.AIDANT_NOM],
          [FORM_FIELDS.AIDANT_STRUCTURE]: formData[FORM_FIELDS.AIDANT_STRUCTURE],
          [FORM_FIELDS.AIDANT_EMAIL]: formData[FORM_FIELDS.AIDANT_EMAIL],
          [FORM_FIELDS.AIDANT_TELEPHONE]: formData[FORM_FIELDS.AIDANT_TELEPHONE],
          // Champs Salesforce pour prescripteur
          ...(FORM_FIELDS.LASTNAME && { [FORM_FIELDS.LASTNAME]: formData[FORM_FIELDS.PRESCRIPTEUR_NOM] }),
          ...(FORM_FIELDS.FIRSTNAME && { [FORM_FIELDS.FIRSTNAME]: formData[FORM_FIELDS.PRESCRIPTEUR_PRENOM] }),
        };
      }

      // Créer payload pour Strapi (conserver les champs d'adresse pour le backend)
      const cleanedPayload = Object.fromEntries(
        Object.entries(payload).filter(([key, value]) =>
          key !== 'undefined' &&
          !['firstname', 'lastname'].includes(key)
        )
      );

      // DEBUG: Vérifier ce qui est filtré
      console.log('🔍 PAYLOAD FILTRÉ POUR STRAPI (cleanedPayload):');
      Object.entries(cleanedPayload).forEach(([key, value]) => {
        console.log(`  - ${key}:`, value);
      });

      console.log('⚠️ CHAMPS FILTRÉS (non envoyés à Strapi):');
      ['firstname', 'lastname'].forEach(key => {
        if (payload[key] !== undefined) {
          console.log(`  - ${key}:`, payload[key], '(filtré)');
        }
      });

      // Log du payload complet avec adresse
      console.log('📤 PAYLOAD COMPLET (avec adresse) pour Salesforce:', {
        ...payload,
        street: payload.street,
        postcode: payload.postcode,
        city: payload.city
      });

      // FORCAGE - Vérifier l'ID dans le payload
      console.log('🔥 PAYLOAD - AccountID =', payload.AccountID);
      console.log('🔥 PAYLOAD ', payload);

      // Vérification spécifique des champs d'adresse
      console.log('🔍 VÉRIFICATION CHAMPS ADRESSE DANS PAYLOAD:');
      console.log('  - street:', payload.street, '(type:', typeof payload.street, ')');
      console.log('  - postcode:', payload.postcode, '(type:', typeof payload.postcode, ')');
      console.log('  - city:', payload.city, '(type:', typeof payload.city, ')');


      // Envoyer à l'API Strapi
      const endpoint = process.env.NEXT_PUBLIC_DEMANDES_ENDPOINT || "/api/demandes";
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: cleanedPayload }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Erreur lors de l'envoi");
      }

      setStatus("success");
      setFormData({
        [FORM_FIELDS.TYPE_DEMANDE]: "Demande de documents",
        [FORM_FIELDS.PRESCRIPTEUR_PRENOM]: "",
        [FORM_FIELDS.PRESCRIPTEUR_NOM]: "",
        [FORM_FIELDS.PRESCRIPTEUR_STRUCTURE]: "",
        [FORM_FIELDS.PRESCRIPTEUR_EMAIL]: "",
        [FORM_FIELDS.PRESCRIPTEUR_TELEPHONE]: "",
        [FORM_FIELDS.PRESCRIPTEUR_MESSAGE]: "",
        [FORM_FIELDS.PATIENT_PRENOM]: "",
        [FORM_FIELDS.PATIENT_NOM]: "",
        [FORM_FIELDS.PATIENT_STRUCTURE]: "",
        [FORM_FIELDS.PATIENT_EMAIL]: "",
        [FORM_FIELDS.PATIENT_TELEPHONE]: "",
        [FORM_FIELDS.PATIENT_REGIME]: "",
        [FORM_FIELDS.PATIENT_DATE_COMMENCEMENT]: "",
        [FORM_FIELDS.PATIENT_ADRESSE]: "",
        [FORM_FIELDS.AIDANT_PRENOM]: "",
        [FORM_FIELDS.AIDANT_NOM]: "",
        [FORM_FIELDS.AIDANT_STRUCTURE]: "",
        [FORM_FIELDS.ADRESSE]: "",
        [FORM_FIELDS.AIDANT_EMAIL]: "",
        [FORM_FIELDS.AIDANT_TELEPHONE]: "",
        [FORM_FIELDS.CONSENT_GDPR]: false,
        [FORM_FIELDS.CONSENT_PHONE]: false,
        [FORM_FIELDS.CONSENT_EMAIL]: false,
      });
      clearErrors();
    } catch (error) {
      console.error("❌ Erreur:", error);
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "Une erreur est survenue");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full py-12 lms-padding-mobile sm:px-6 lg:px-8 bg-muted" id="contact">
      <div className="max-w-4xl mx-auto">
        {/* Titre */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold font-playfair text-gray-900 mb-4">{titre}</h2>
          <p className="text-lg md:text-xl text-gray-600">
            Exprimez votre demande en quelques clics
          </p>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-10" noValidate>
          {/* Type de demande */}
          <div className="flex justify-center">
            <label htmlFor="type_demande" className="sr-only">
              Type de demande
            </label>
            <div className="relative w-full max-w-md">
              <select
                id={FORM_FIELDS.TYPE_DEMANDE}
                name={FORM_FIELDS.TYPE_DEMANDE}
                value={formData[FORM_FIELDS.TYPE_DEMANDE]}
                onChange={handleChange}
                required
                className="w-full h-14 bg-white border-2 border-primaryGreen rounded-md shadow-[0_15px_30px_rgba(0,0,0,0.12)] text-gray-500 pr-10 pl-12 appearance-none focus:outline-none focus:ring-0"
              >
                {DEMANDE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {formData[FORM_FIELDS.TYPE_DEMANDE] === "Autre" && (
                <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center z-10">
                  <Image
                    width={20}
                    height={20}
                    src={ICONS.typeAutre}
                    alt="Autre"
                    className="w-5 h-5"
                  />
                </span>
              )}
              {formData[FORM_FIELDS.TYPE_DEMANDE] === "Demande de documents" && (
                <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center z-10">
                  <Image
                    width={20}
                    height={20}
                    src={ICONS.typeDocuments}
                    alt="Demande de documents"
                    className="w-5 h-5"
                  />
                </span>
              )}
              {formData[FORM_FIELDS.TYPE_DEMANDE] === "Demande de devis" && (
                <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center z-10">
                  <Image
                    width={20}
                    height={20}
                    src={ICONS.typeDevis}
                    alt="Demande de devis"
                    className="w-5 h-5"
                  />
                </span>
              )}
              {formData[FORM_FIELDS.TYPE_DEMANDE] === "Prescrire" && (
                <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center z-10">
                  <Image
                    width={20}
                    height={20}
                    src={ICONS.typePrescrire}
                    alt="Prescrire"
                    className="w-5 h-5"
                  />
                </span>
              )}
              <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center z-10">
                <svg
                  className="w-5 h-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </div>
          </div>

          {/* Afficher les champs selon le type de demande */}
          {formData[FORM_FIELDS.TYPE_DEMANDE] && (
            <>
              {/* Section Prescripteur (pour tous les types) */}
              <div className="mt-4">
                <div className="flex items-center gap-4 mb-6">
                  <h3 className="text-xl font-semibold text-primaryGreen">Prescripteur</h3>
                  <div className="flex-1 h-px bg-gray-300" />
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Input
                      id={FORM_FIELDS.PRESCRIPTEUR_STRUCTURE}
                      name={FORM_FIELDS.PRESCRIPTEUR_STRUCTURE}
                      type="text"
                      placeholder="Nom de la structure *"
                      required
                      value={formData[FORM_FIELDS.PRESCRIPTEUR_STRUCTURE]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PRESCRIPTEUR_STRUCTURE, e.target.value)}
                      error={errors[FORM_FIELDS.PRESCRIPTEUR_STRUCTURE]}
                      iconName={ICONS.structure}
                    />
                  </div>
                  <Input
                    id={FORM_FIELDS.PRESCRIPTEUR_PRENOM}
                    name={FORM_FIELDS.PRESCRIPTEUR_PRENOM}
                    type="text"
                    placeholder="Prénom *"
                    required
                    value={formData[FORM_FIELDS.PRESCRIPTEUR_PRENOM]}
                    onChange={handleChange}
                    onBlur={(e) => validateField(FORM_FIELDS.PRESCRIPTEUR_PRENOM, e.target.value)}
                    error={errors[FORM_FIELDS.PRESCRIPTEUR_PRENOM]}
                    iconName={ICONS.user}
                  />
                  <Input
                    id={FORM_FIELDS.PRESCRIPTEUR_NOM}
                    name={FORM_FIELDS.PRESCRIPTEUR_NOM}
                    type="text"
                    placeholder="Nom *"
                    required
                    value={formData[FORM_FIELDS.PRESCRIPTEUR_NOM]}
                    onChange={handleChange}
                    onBlur={(e) => validateField(FORM_FIELDS.PRESCRIPTEUR_NOM, e.target.value)}
                    error={errors[FORM_FIELDS.PRESCRIPTEUR_NOM]}
                    iconName={ICONS.user}
                  />
                  <Input
                    id={FORM_FIELDS.PRESCRIPTEUR_EMAIL}
                    name={FORM_FIELDS.PRESCRIPTEUR_EMAIL}
                    type="email"
                    placeholder="Email *"
                    required
                    value={formData[FORM_FIELDS.PRESCRIPTEUR_EMAIL]}
                    onChange={handleChange}
                    onBlur={(e) => validateField(FORM_FIELDS.PRESCRIPTEUR_EMAIL, e.target.value)}
                    error={errors[FORM_FIELDS.PRESCRIPTEUR_EMAIL]}
                    iconName={ICONS.email}
                  />
                  <Input
                    id={FORM_FIELDS.PRESCRIPTEUR_TELEPHONE}
                    name={FORM_FIELDS.PRESCRIPTEUR_TELEPHONE}
                    type="tel"
                    placeholder="Téléphone *"
                    required
                    value={formData[FORM_FIELDS.PRESCRIPTEUR_TELEPHONE]}
                    onChange={handleChange}
                    onBlur={(e) => validateField(FORM_FIELDS.PRESCRIPTEUR_TELEPHONE, e.target.value)}
                    error={errors[FORM_FIELDS.PRESCRIPTEUR_TELEPHONE]}
                    iconName={ICONS.phone}
                  />
                  {/* Champ Adresse pour "Autre" et "Demande de documents" */}
                  {(formData[FORM_FIELDS.TYPE_DEMANDE] === "Autre" || formData[FORM_FIELDS.TYPE_DEMANDE] === "Demande de documents") && (
                    <div className="sm:col-span-2 relative">
                      <Input
                        id={FORM_FIELDS.ADRESSE}
                        name={FORM_FIELDS.ADRESSE}
                        placeholder="Adresse de livraison"
                        required
                        iconName={ICONS.adresse}
                        value={formData[FORM_FIELDS.ADRESSE]}
                        onChange={handleChange}
                        error={errors[FORM_FIELDS.ADRESSE]}
                        onFocus={() =>
                          predictions.length > 0 && setShowPredictions(true)
                        }
                        onBlur={(e) => {
                          validateField(FORM_FIELDS.ADRESSE, e.target.value);
                          setTimeout(() => setShowPredictions(false), 300);
                        }}
                        autoComplete="off"
                      />
                      {showPredictions && predictions.length > 0 && (
                        <ul
                          className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1"
                          onMouseDown={(e) => {
                            e.preventDefault();
                          }}
                        >
                          {predictions.map((prediction) => {
                            const placeId = prediction.placeId;
                            return (
                              <li
                                key={placeId}
                                role="button"
                                tabIndex={0}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                onMouseDown={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowPredictions(false);

                                  setFormData(prev => ({ ...prev, [FORM_FIELDS.ADRESSE]: prediction.description }));
                                  validateField(FORM_FIELDS.ADRESSE, prediction.description);

                                  try {
                                    const data = await geoService.getMap(placeId);
                                    console.log('🔍 API getMap - Réponse complète:', data);

                                    const fullDescription = prediction.description;
                                    console.log('🔍 Adresse client à parser:', fullDescription);

                                    const addressPattern = /^(.+?)\s*,?\s*(\d{5})\s+(.+)$/;
                                    const match = fullDescription.match(addressPattern);

                                    let extractedStreet = '';
                                    let extractedPostcode = '';
                                    let extractedCity = '';

                                    if (match) {
                                      extractedStreet = match[1].trim().replace(/,\s*$/, '');
                                      extractedPostcode = match[2];
                                      extractedCity = match[3].split(',')[0].trim();
                                      console.log('✅ Parsing réussi - street:', extractedStreet, '| postcode:', extractedPostcode, '| city:', extractedCity);
                                    } else {
                                      console.log('⚠️ Pattern non trouvé, appel geocoding API...');
                                      try {
                                        const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullDescription)}&limit=1`);
                                        const geocodingData = await response.json();
                                        if (geocodingData.features && geocodingData.features.length > 0) {
                                          const feature = geocodingData.features[0].properties;
                                          extractedStreet = feature.name || '';
                                          extractedPostcode = feature.postcode || '';
                                          extractedCity = feature.city || '';
                                          console.log('✅ Geocoding réussi - street:', extractedStreet, '| postcode:', extractedPostcode, '| city:', extractedCity);
                                        }
                                      } catch (error) {
                                        console.warn('⚠️ Erreur geocoding:', error);
                                        extractedStreet = fullDescription;
                                      }
                                    }
                                    const idSalesforce = await getAgencyIdSalesforce(placeId);
                                    console.log("✅ ID Salesforce agence final:", idSalesforce);

                                    setAddressDetails({
                                      street: extractedStreet,
                                      postcode: extractedPostcode,
                                      city: extractedCity,
                                      idSalesforce,
                                    });
                                  } catch (err) {
                                    console.error('Erreur:', err);
                                  }
                                }}
                              >
                                {prediction.description}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Textarea
                      id={FORM_FIELDS.PRESCRIPTEUR_MESSAGE}
                      name={FORM_FIELDS.PRESCRIPTEUR_MESSAGE}
                      placeholder="Message *"
                      required
                      value={formData[FORM_FIELDS.PRESCRIPTEUR_MESSAGE]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PRESCRIPTEUR_MESSAGE, e.target.value)}
                      error={errors[FORM_FIELDS.PRESCRIPTEUR_MESSAGE]}
                      rows={4}
                      iconName={ICONS.message}
                    />
                  </div>
                </div>
              </div>

              {/* Section Patient (pour Demande de devis) */}
              {formData[FORM_FIELDS.TYPE_DEMANDE] === "Demande de devis" && (
                <div className="mt-8">
                  <div className="flex items-center gap-4 mb-6">
                    <h3 className="text-xl font-semibold text-primaryGreen">Patient</h3>
                    <div className="flex-1 h-px bg-gray-300" />
                  </div>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Input
                        id={FORM_FIELDS.PATIENT_STRUCTURE}
                        name={FORM_FIELDS.PATIENT_STRUCTURE}
                        type="text"
                        placeholder="Nom de la structure *"
                        required
                        value={formData[FORM_FIELDS.PATIENT_STRUCTURE]}
                        onChange={handleChange}
                        onBlur={(e) => validateField(FORM_FIELDS.PATIENT_STRUCTURE, e.target.value)}
                        error={errors[FORM_FIELDS.PATIENT_STRUCTURE]}
                        iconName={ICONS.structure}
                      />
                    </div>
                    <Input
                      id={FORM_FIELDS.PATIENT_PRENOM}
                      name={FORM_FIELDS.PATIENT_PRENOM}
                      type="text"
                      placeholder="Prénom *"
                      required
                      value={formData[FORM_FIELDS.PATIENT_PRENOM]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PATIENT_PRENOM, e.target.value)}
                      error={errors[FORM_FIELDS.PATIENT_PRENOM]}
                      iconName={ICONS.user}
                    />
                    <Input
                      id={FORM_FIELDS.PATIENT_NOM}
                      name={FORM_FIELDS.PATIENT_NOM}
                      type="text"
                      placeholder="Nom *"
                      required
                      value={formData[FORM_FIELDS.PATIENT_NOM]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PATIENT_NOM, e.target.value)}
                      error={errors[FORM_FIELDS.PATIENT_NOM]}
                      iconName={ICONS.user}
                    />
                    <Input
                      id={FORM_FIELDS.PATIENT_EMAIL}
                      name={FORM_FIELDS.PATIENT_EMAIL}
                      type="email"
                      placeholder="Email *"
                      required
                      value={formData[FORM_FIELDS.PATIENT_EMAIL]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PATIENT_EMAIL, e.target.value)}
                      error={errors[FORM_FIELDS.PATIENT_EMAIL]}
                      iconName={ICONS.email}
                    />
                    <Input
                      id={FORM_FIELDS.PATIENT_TELEPHONE}
                      name={FORM_FIELDS.PATIENT_TELEPHONE}
                      type="tel"
                      placeholder="Téléphone *"
                      required
                      value={formData[FORM_FIELDS.PATIENT_TELEPHONE]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PATIENT_TELEPHONE, e.target.value)}
                      error={errors[FORM_FIELDS.PATIENT_TELEPHONE]}
                      iconName={ICONS.phone}
                    />
                    <Input
                      id={FORM_FIELDS.PATIENT_REGIME}
                      name={FORM_FIELDS.PATIENT_REGIME}
                      type="text"
                      placeholder="Régime *"
                      required
                      value={formData[FORM_FIELDS.PATIENT_REGIME]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PATIENT_REGIME, e.target.value)}
                      error={errors[FORM_FIELDS.PATIENT_REGIME]}
                      iconName={ICONS.salad}
                    />
                    <div className="relative">
                      <Input
                        id={FORM_FIELDS.PATIENT_DATE_COMMENCEMENT}
                        name={FORM_FIELDS.PATIENT_DATE_COMMENCEMENT}
                        type="date"
                        placeholder="Date de commencement"
                        required
                        value={formData[FORM_FIELDS.PATIENT_DATE_COMMENCEMENT]}
                        onChange={handleChange}
                        onBlur={(e) => validateField(FORM_FIELDS.PATIENT_DATE_COMMENCEMENT, e.target.value)}
                        error={errors[FORM_FIELDS.PATIENT_DATE_COMMENCEMENT]}
                        iconName={ICONS.date}
                      />
                    </div>
                    <div className="sm:col-span-2 relative">
                      <Input
                        id={FORM_FIELDS.PATIENT_ADRESSE}
                        name={FORM_FIELDS.PATIENT_ADRESSE}
                        placeholder="Adresse de livraison *"
                        required
                        iconName={ICONS.adresse}
                        value={formData[FORM_FIELDS.PATIENT_ADRESSE]}
                        onChange={handleChange}
                        error={errors[FORM_FIELDS.PATIENT_ADRESSE]}
                        onFocus={() =>
                          patientPredictions.length > 0 && setShowPatientPredictions(true)
                        }
                        onBlur={(e) => {
                          validateField(FORM_FIELDS.PATIENT_ADRESSE, e.target.value);
                          setTimeout(() => setShowPatientPredictions(false), 300);
                        }}
                        autoComplete="off"
                      />
                      {showPatientPredictions && patientPredictions.length > 0 && (
                        <ul
                          className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1"
                          onMouseDown={(e) => {
                            e.preventDefault();
                          }}
                        >
                          {patientPredictions.map((prediction) => {
                            const placeId = prediction.placeId;
                            return (
                              <li
                                key={placeId}
                                role="button"
                                tabIndex={0}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                onMouseDown={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowPatientPredictions(false);

                                  setFormData(prev => ({ ...prev, [FORM_FIELDS.PATIENT_ADRESSE]: prediction.description }));
                                  validateField(FORM_FIELDS.PATIENT_ADRESSE, prediction.description);

                                  try {
                                    const data = await geoService.getMap(placeId);
                                    console.log('🔍 API getMap - Réponse complète:', data);

                                    const fullDescription = prediction.description;
                                    console.log('🔍 Adresse patient à parser:', fullDescription);

                                    const addressPattern = /^(.+?)\s*,?\s*(\d{5})\s+(.+)$/;
                                    const match = fullDescription.match(addressPattern);

                                    let extractedStreet = '';
                                    let extractedPostcode = '';
                                    let extractedCity = '';

                                    if (match) {
                                      extractedStreet = match[1].trim().replace(/,\s*$/, '');
                                      extractedPostcode = match[2];
                                      extractedCity = match[3].split(',')[0].trim();
                                      console.log('✅ Parsing réussi - street:', extractedStreet, '| postcode:', extractedPostcode, '| city:', extractedCity);
                                    } else {
                                      console.log('⚠️ Pattern non trouvé, appel geocoding API...');
                                      try {
                                        const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullDescription)}&limit=1`);
                                        const geocodingData = await response.json();
                                        if (geocodingData.features && geocodingData.features.length > 0) {
                                          const feature = geocodingData.features[0].properties;
                                          extractedStreet = feature.name || '';
                                          extractedPostcode = feature.postcode || '';
                                          extractedCity = feature.city || '';
                                          console.log('✅ Geocoding réussi - street:', extractedStreet, '| postcode:', extractedPostcode, '| city:', extractedCity);
                                        }
                                      } catch (error) {
                                        console.warn('⚠️ Erreur geocoding:', error);
                                        extractedStreet = fullDescription;
                                      }
                                    }
                                    const idSalesforce = await getAgencyIdSalesforce(placeId);
                                    console.log("✅ ID Salesforce agence final:", idSalesforce);
                                    setPatientAddressDetails({
                                      street: extractedStreet,
                                      postcode: extractedPostcode,
                                      city: extractedCity,
                                      idSalesforce,
                                    });
                                  } catch (err) {
                                    console.error('Erreur:', err);
                                  }
                                }}
                              >
                                {prediction.description}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Section Patient (pour Prescrire) */}
              {formData[FORM_FIELDS.TYPE_DEMANDE] === "Prescrire" && (
                <div className="mt-8">
                  <div className="flex items-center gap-4 mb-6">
                    <h3 className="text-xl font-semibold text-primaryGreen">Patient</h3>
                    <div className="flex-1 h-px bg-gray-300" />
                  </div>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Input
                        id={FORM_FIELDS.PATIENT_STRUCTURE}
                        name={FORM_FIELDS.PATIENT_STRUCTURE}
                        type="text"
                        placeholder="Nom de la structure *"
                        required
                        value={formData[FORM_FIELDS.PATIENT_STRUCTURE]}
                        onChange={handleChange}
                        onBlur={(e) => validateField(FORM_FIELDS.PATIENT_STRUCTURE, e.target.value)}
                        error={errors[FORM_FIELDS.PATIENT_STRUCTURE]}
                        iconName={ICONS.structure}
                      />
                    </div>
                    <Input
                      id={FORM_FIELDS.PATIENT_PRENOM}
                      name={FORM_FIELDS.PATIENT_PRENOM}
                      type="text"
                      placeholder="Prénom *"
                      required
                      value={formData[FORM_FIELDS.PATIENT_PRENOM]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PATIENT_PRENOM, e.target.value)}
                      error={errors[FORM_FIELDS.PATIENT_PRENOM]}
                      iconName={ICONS.user}
                    />
                    <Input
                      id={FORM_FIELDS.PATIENT_NOM}
                      name={FORM_FIELDS.PATIENT_NOM}
                      type="text"
                      placeholder="Nom *"
                      required
                      value={formData[FORM_FIELDS.PATIENT_NOM]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PATIENT_NOM, e.target.value)}
                      error={errors[FORM_FIELDS.PATIENT_NOM]}
                      iconName={ICONS.user}
                    />
                    <Input
                      id={FORM_FIELDS.PATIENT_EMAIL}
                      name={FORM_FIELDS.PATIENT_EMAIL}
                      type="email"
                      placeholder="Email *"
                      required
                      value={formData[FORM_FIELDS.PATIENT_EMAIL]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PATIENT_EMAIL, e.target.value)}
                      error={errors[FORM_FIELDS.PATIENT_EMAIL]}
                      iconName={ICONS.email}
                    />
                    <Input
                      id={FORM_FIELDS.PATIENT_TELEPHONE}
                      name={FORM_FIELDS.PATIENT_TELEPHONE}
                      type="tel"
                      placeholder="Téléphone *"
                      required
                      value={formData[FORM_FIELDS.PATIENT_TELEPHONE]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PATIENT_TELEPHONE, e.target.value)}
                      error={errors[FORM_FIELDS.PATIENT_TELEPHONE]}
                      iconName={ICONS.phone}
                    />
                    <Input
                      id={FORM_FIELDS.PATIENT_REGIME}
                      name={FORM_FIELDS.PATIENT_REGIME}
                      type="text"
                      placeholder="Régime *"
                      required
                      value={formData[FORM_FIELDS.PATIENT_REGIME]}
                      onChange={handleChange}
                      onBlur={(e) => validateField(FORM_FIELDS.PATIENT_REGIME, e.target.value)}
                      error={errors[FORM_FIELDS.PATIENT_REGIME]}
                      iconName={ICONS.salad}
                    />
                    <div className="relative">
                      <Input
                        id={FORM_FIELDS.PATIENT_DATE_COMMENCEMENT}
                        name={FORM_FIELDS.PATIENT_DATE_COMMENCEMENT}
                        type="date"
                        placeholder="Date de commencement"
                        required
                        value={formData[FORM_FIELDS.PATIENT_DATE_COMMENCEMENT]}
                        onChange={handleChange}
                        onBlur={(e) => validateField(FORM_FIELDS.PATIENT_DATE_COMMENCEMENT, e.target.value)}
                        error={errors[FORM_FIELDS.PATIENT_DATE_COMMENCEMENT]}
                        iconName={ICONS.date}
                      />
                    </div>
                    <div className="sm:col-span-2 relative">
                      <Input
                        id={FORM_FIELDS.PATIENT_ADRESSE}
                        name={FORM_FIELDS.PATIENT_ADRESSE}
                        placeholder="Adresse de livraison *"
                        required
                        iconName={ICONS.adresse}
                        value={formData[FORM_FIELDS.PATIENT_ADRESSE]}
                        onChange={handleChange}
                        error={errors[FORM_FIELDS.PATIENT_ADRESSE]}
                        onFocus={() =>
                          patientPredictions.length > 0 && setShowPatientPredictions(true)
                        }
                        onBlur={(e) => {
                          validateField(FORM_FIELDS.PATIENT_ADRESSE, e.target.value);
                          setTimeout(() => setShowPatientPredictions(false), 300);
                        }}
                        autoComplete="off"
                      />
                      {showPatientPredictions && patientPredictions.length > 0 && (
                        <ul
                          className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1"
                          onMouseDown={(e) => {
                            e.preventDefault();
                          }}
                        >
                          {patientPredictions.map((prediction) => {
                            const placeId = prediction.placeId;
                            return (
                              <li
                                key={placeId}
                                role="button"
                                tabIndex={0}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                onMouseDown={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowPatientPredictions(false);

                                  setFormData(prev => ({ ...prev, [FORM_FIELDS.PATIENT_ADRESSE]: prediction.description }));
                                  validateField(FORM_FIELDS.PATIENT_ADRESSE, prediction.description);

                                  try {
                                    const data = await geoService.getMap(placeId);
                                    console.log('🔍 API getMap - Réponse complète (Prescrire):', data);

                                    const fullDescription = prediction.description;
                                    console.log('🔍 Adresse patient à parser (Prescrire):', fullDescription);

                                    const addressPattern = /^(.+?)\s*,?\s*(\d{5})\s+(.+)$/;
                                    const match = fullDescription.match(addressPattern);

                                    let extractedStreet = '';
                                    let extractedPostcode = '';
                                    let extractedCity = '';

                                    if (match) {
                                      extractedStreet = match[1].trim().replace(/,\s*$/, '');
                                      extractedPostcode = match[2];
                                      extractedCity = match[3].split(',')[0].trim();
                                      console.log('✅ Parsing réussi (Prescrire) - street:', extractedStreet, '| postcode:', extractedPostcode, '| city:', extractedCity);
                                    } else {
                                      console.log('⚠️ Pattern non trouvé (Prescrire), appel geocoding API...');
                                      try {
                                        const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullDescription)}&limit=1`);
                                        const geocodingData = await response.json();
                                        if (geocodingData.features && geocodingData.features.length > 0) {
                                          const feature = geocodingData.features[0].properties;
                                          extractedStreet = feature.name || '';
                                          extractedPostcode = feature.postcode || '';
                                          extractedCity = feature.city || '';
                                          console.log('✅ Geocoding réussi (Prescrire) - street:', extractedStreet, '| postcode:', extractedPostcode, '| city:', extractedCity);
                                        }
                                      } catch (error) {
                                        console.warn('⚠️ Erreur geocoding (Prescrire):', error);
                                        extractedStreet = fullDescription;
                                      }
                                    }
                                    const idSalesforce = await getAgencyIdSalesforce(placeId);
                                    console.log("✅ ID Salesforce agence final:", idSalesforce);
                                    setPatientAddressDetails({
                                      street: extractedStreet,
                                      postcode: extractedPostcode,
                                      city: extractedCity,
                                      idSalesforce,
                                    });
                                  } catch (err) {
                                    console.error('Erreur (Prescrire):', err);
                                  }
                                }}
                              >
                                {prediction.description}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Section Aidant (pour Prescrire) */}
              {formData[FORM_FIELDS.TYPE_DEMANDE] === "Prescrire" && (
                <div className="mt-8">
                  <div className="flex items-center gap-4 mb-6">
                    <h3 className="text-xl font-semibold text-primaryGreen">Aidant</h3>
                    <div className="flex-1 h-px bg-gray-300" />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAidant(!showAidant)}
                      className="ml-4"
                    >
                      {showAidant ? "Masquer" : "Ajouter"} Aidant
                    </Button>
                  </div>

                  {showAidant && (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <Input
                          id={FORM_FIELDS.AIDANT_STRUCTURE}
                          name={FORM_FIELDS.AIDANT_STRUCTURE}
                          type="text"
                          placeholder="Nom de la structure *"
                          required={showAidant}
                          value={formData[FORM_FIELDS.AIDANT_STRUCTURE]}
                          onChange={handleChange}
                          onBlur={(e) => validateField(FORM_FIELDS.AIDANT_STRUCTURE, e.target.value)}
                          error={errors[FORM_FIELDS.AIDANT_STRUCTURE]}
                          iconName={ICONS.structure}
                        />
                      </div>
                      <Input
                        id={FORM_FIELDS.AIDANT_PRENOM}
                        name={FORM_FIELDS.AIDANT_PRENOM}
                        type="text"
                        placeholder="Prénom *"
                        required={showAidant}
                        value={formData[FORM_FIELDS.AIDANT_PRENOM]}
                        onChange={handleChange}
                        onBlur={(e) => validateField(FORM_FIELDS.AIDANT_PRENOM, e.target.value)}
                        error={errors[FORM_FIELDS.AIDANT_PRENOM]}
                        iconName={ICONS.user}
                      />
                      <Input
                        id={FORM_FIELDS.AIDANT_NOM}
                        name={FORM_FIELDS.AIDANT_NOM}
                        type="text"
                        placeholder="Nom *"
                        required={showAidant}
                        value={formData[FORM_FIELDS.AIDANT_NOM]}
                        onChange={handleChange}
                        onBlur={(e) => validateField(FORM_FIELDS.AIDANT_NOM, e.target.value)}
                        error={errors[FORM_FIELDS.AIDANT_NOM]}
                        iconName={ICONS.user}
                      />
                      <Input
                        id={FORM_FIELDS.AIDANT_EMAIL}
                        name={FORM_FIELDS.AIDANT_EMAIL}
                        type="email"
                        placeholder="Email *"
                        required={showAidant}
                        value={formData[FORM_FIELDS.AIDANT_EMAIL]}
                        onChange={handleChange}
                        onBlur={(e) => validateField(FORM_FIELDS.AIDANT_EMAIL, e.target.value)}
                        error={errors[FORM_FIELDS.AIDANT_EMAIL]}
                        iconName={ICONS.email}
                      />
                      <Input
                        id={FORM_FIELDS.AIDANT_TELEPHONE}
                        name={FORM_FIELDS.AIDANT_TELEPHONE}
                        type="tel"
                        placeholder="Téléphone *"
                        required={showAidant}
                        value={formData[FORM_FIELDS.AIDANT_TELEPHONE]}
                        onChange={handleChange}
                        onBlur={(e) => validateField(FORM_FIELDS.AIDANT_TELEPHONE, e.target.value)}
                        error={errors[FORM_FIELDS.AIDANT_TELEPHONE]}
                        iconName={ICONS.phone}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Champ hidden pour l'indicatif téléphonique */}
          <input
            type="hidden"
            name={FORM_FIELDS.PHONE_INDICATOR}
            value="FR"
          />

          {/* Consentements */}
          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-4 mb-2">
              <div className="flex-1 h-px bg-gray-300" />
            </div>
            <Checkbox
              id={FORM_FIELDS.CONSENT_GDPR}
              name={FORM_FIELDS.CONSENT_GDPR}
              label={
                <>
                  J'accepte que mes données soient utilisées pour traiter ma demande (<a href="https://www.les-menus-services.com/politique-de-confidentialite" target="_blank" rel="noopener noreferrer" className="underline hover:text-primaryGreen">voir la politique de confidentialité</a>).
                </>
              }
              checked={formData[FORM_FIELDS.CONSENT_GDPR] as boolean}
              onChange={handleChange}
              error={errors[FORM_FIELDS.CONSENT_GDPR]}
              required
            />
            <p className="text-sm text-gray-600">
              Vos coordonnées servent uniquement à répondre à votre demande. La loi interdit de vous adresser des offres commerciales par téléphone ou par e-mail sans votre consentement préalable. Si vous souhaitez recevoir nos offres, cochez la ou les cases correspondantes (facultatif) :
            </p>
            <Checkbox
              id={FORM_FIELDS.CONSENT_PHONE}
              name={FORM_FIELDS.CONSENT_PHONE}
              label="Je donne mon consentement pour recevoir des offres par téléphone"
              checked={formData[FORM_FIELDS.CONSENT_PHONE] as boolean}
              onChange={handleChange}
              error={errors[FORM_FIELDS.CONSENT_PHONE]}
            />
            <Checkbox
              id={FORM_FIELDS.CONSENT_EMAIL}
              name={FORM_FIELDS.CONSENT_EMAIL}
              label="Je donne mon consentement pour recevoir des offres par e-mail"
              checked={formData[FORM_FIELDS.CONSENT_EMAIL] as boolean}
              onChange={handleChange}
              error={errors[FORM_FIELDS.CONSENT_EMAIL]}
            />
          </div>

          {/* Messages */}
          {status === "error" && errorMessage && (
            <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg">
              ❌ {errorMessage}
            </div>
          )}

          {status === "success" && (
            <div className="p-4 text-sm text-green-700 bg-green-100 rounded-lg">
              ✅ Votre demande a été envoyée avec succès. Nous vous recontacterons rapidement.
            </div>
          )}

          {/* Bouton */}
          <div className="flex justify-center pt-6">
            <Button
              type="submit"
              variant="default"
              size="lg"
              disabled={isSubmitting}
              className="h-14 px-12 text-lg font-semibold uppercase text-white bg-secondary hover:bg-primaryGreen disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {isSubmitting ? "Envoi en cours..." : "Envoyer la demande"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
